import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PagesContainer from "../../components/UI/PagesContainer";
import { usePage } from "../../hooks/usePage";
import { DoctorAgendaList } from "./DoctorAgenda/DoctorAgendaList";
import { DoctorAgendaListThreeDays } from "./DoctorAgenda/DoctorAgendaListThreeDays";
import { useDoctorAgenda } from "./DoctorAgenda/useDoctorAgenda";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const translation_path = "page.doctorAgenda.";

type ViewModeParams = {
  viewMode: "today" | "three_days" | "custom";
};

const DoctorAgendaPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useParams<ViewModeParams>();

  const viewMode = params.viewMode || "today";

  const { t } = useTranslation();

  const { pageTitle } = usePage();

  const { dayjsBrazil } = useDoctorAgenda();

  const [customDate, setCustomDate] = useState<dayjs.Dayjs | null>(
    searchParams.get("date")
      ? dayjsBrazil(searchParams.get("date"))
      : dayjsBrazil()
  );

  const [refreshKey, setRefreshKey] = useState(0);

  const showFree = searchParams.get("showFree") === "true";

  const handleShowFreeChange = useCallback(
    (checked: boolean) => {
      const newSearchParams = new URLSearchParams(searchParams);

      if (checked) {
        newSearchParams.set("showFree", "true");
      } else {
        newSearchParams.delete("showFree");
      }

      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const dates = useMemo(() => {
    if (viewMode === "custom" && customDate) {
      return {
        calendarDateStart: customDate.startOf("day").toDate(),
        calendarDateEnd: customDate.endOf("day").toDate(),
      };
    }

    const start = dayjsBrazil();
    let end = dayjsBrazil();

    if (viewMode === "three_days") {
      end = end.add(3, "day");
    }

    return {
      calendarDateStart: start.startOf("day").toDate(),
      calendarDateEnd: end.endOf("day").toDate(),
    };
  }, [viewMode, customDate, dayjsBrazil]);

  const handleAlignment = (
    _event: React.MouseEvent<HTMLElement>,
    newAlignment: ViewModeParams["viewMode"] | null
  ) => {
    if (newAlignment !== null) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("date");

      navigate(`/doctor-agenda/${newAlignment}?${newSearchParams.toString()}`, { replace: true });
    }
  };

  const handleSetCustomDate = useCallback(
    (date: Dayjs | null) => {
      if (!date) return;
      setCustomDate(date);

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("date", date.format("YYYY-MM-DD"));

      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const refreshHandler = () => {
    setRefreshKey(refreshKey + 1);
  };

  useEffect(() => {
    if (!params.viewMode) {
      navigate(`/doctor-agenda/today`, { replace: true });
    }
  }, [params.viewMode, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey((prevKey) => prevKey + 1);
    }, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <PagesContainer title={pageTitle || t(translation_path + "title")}>
      <Stack spacing={2}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleAlignment}
            aria-label="text alignment"
          >
            <ToggleButton
              value="today"
              aria-label="left aligned"
              sx={{
                whiteSpace: "nowrap",
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: "primary.main",
                  color: "#fff",
                  "& .MuiTypography-root": {
                    fontWeight: "bold",
                  },
                },
              }}
            >
              <Typography variant="caption">
                {t(translation_path + "today")}
              </Typography>
            </ToggleButton>
            <ToggleButton
              value="three_days"
              aria-label="centered"
              sx={{
                whiteSpace: "nowrap",
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: "primary.main",
                  color: "#fff",
                  "& .MuiTypography-root": {
                    fontWeight: "bold",
                  },
                },
              }}
            >
              <Typography variant="caption">
                {t(translation_path + "three_days")}
              </Typography>
            </ToggleButton>
            <ToggleButton
              value="custom"
              aria-label="right aligned"
              sx={{
                whiteSpace: "nowrap",
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: "primary.main",
                  color: "#fff",
                  "& .MuiTypography-root": {
                    fontWeight: "bold",
                  },
                },
              }}
            >
              <Typography variant="caption">
                {t(translation_path + "select_date")}
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={showFree}
                onChange={(_event, checked) => handleShowFreeChange(checked)}
              />
            }
            label={
              <Typography variant="caption">
                {t(translation_path + "show_free")}
              </Typography>
            }
            sx={{ whiteSpace: "nowrap", ml: 1, mr: "auto" }}
          />
          <Tooltip title={t(translation_path + "refresh") || "Atualizar"}>
            <IconButton sx={{ minWidth: 40 }} onClick={refreshHandler}>
              <RefreshRoundedIcon color="primary" />
            </IconButton>
          </Tooltip>
        </Box>

        {viewMode === "today" && (
          <DoctorAgendaList
            calendarDateStart={dates.calendarDateStart}
            calendarDateEnd={dates.calendarDateEnd}
            showFree={showFree}
            key={refreshKey}
          />
        )}

        {viewMode === "three_days" && (
          <DoctorAgendaListThreeDays
            calendarDateStart={dates.calendarDateStart}
            calendarDateEnd={dates.calendarDateEnd}
            showFree={showFree}
            key={refreshKey}
          />
        )}

        {viewMode === "custom" && (
          <DoctorAgendaList
            calendarDateStart={dates.calendarDateStart}
            calendarDateEnd={dates.calendarDateEnd}
            isCustomDate
            onDateChange={handleSetCustomDate}
            showFree={showFree}
            key={refreshKey}
          />
        )}
      </Stack>
    </PagesContainer>
  );
};

export default DoctorAgendaPage;
